// DepositRequestsTable.jsx
import React, { useMemo } from "react";
import StatusBadge from "./StatusBadge";
import { API_URL } from "helper/Constants";
import { buildUrl } from "helper/buildUrl";


function DepositRequestsTable({
  requests,
  showActions = false,
  onApprove,
  onReject,
  processing = false,
  paymentMethodsMap,
}) {
  const rows = useMemo(() => requests ?? [], [requests]);

  const resolveMethodName = (row) => {
    // If backend returns populated method object: row.paymentMethod?.name
    if (row?.paymentMethod && typeof row.paymentMethod === "object") {
      return row.paymentMethod.name || row.paymentMethod._id || "—";
    }
    // If only id is present -> try mapping
    if (paymentMethodsMap && row?.paymentMethod && paymentMethodsMap[row.paymentMethod]) {
      return paymentMethodsMap[row.paymentMethod].name || row.paymentMethod;
    }
    return row?.paymentMethod || "—";
  };

  const resolveUsername = (row) => row?.username || row?.user?.username || "—";

  const resolveProcessedBy = (row) =>
    row?.processedBy?.username || row?.processedBy?.email || "—";

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      return d.toLocaleString();
    } catch {
      return value;
    }
  };

  const fileUrl = (apiBase, p) => {
    if (!p) return "";
    const cleanBase = apiBase.replace(/\/$/, "");
    const cleanPath = p.replace(/\\/g, "/").replace(/^\/?/, ""); // fix Windows slashes + leading slash
    return `${cleanBase}/${cleanPath}`;
  };

  return (
    <div className="overflow-x-auto border border-slate-700 rounded-lg">
      <table className="min-w-full  divide-y divide-slate-700">
        <thead className="bg-slate-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">User</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Method</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Proof</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Processed By</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Rejection Reason</th>
            {showActions && (
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className=" divide-y divide-slate-800">
          {rows.map((row) => (
            <tr key={row._id}>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">{resolveUsername(row)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                {typeof row.amount === "number" ? row.amount.toFixed(2) : row.amount}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                {resolveMethodName(row)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                {row.screenshot?.path ? (
                  <a
                    href={buildUrl(API_URL, row.screenshot.path)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    {/* Image preview */}
                    <img
                      src={buildUrl(API_URL, row.screenshot.path)}
                      alt="Deposit Proof"
                      width="200"
                      height="200"
                      className="object-cover rounded-md"
                    />
                  </a>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>

              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                {formatDate(row.createdAt)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                {resolveProcessedBy(row)}
              </td>
              <td className="px-4 py-3 text-sm text-red-800 max-w-xs">
                {row.status === "rejected" ? row.rejectionReason || "—" : "—"}
              </td>

              {showActions && (
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                  <div className="inline-flex gap-2">
                    <button
                      className="px-3 py-1.5 rounded bg-green-500/80 hover:bg-green-500 text-slate-900 font-semibold disabled:opacity-50"
                      disabled={processing}
                      onClick={() => onApprove?.(row._id)}
                      title="Approve"
                    >
                      Approve
                    </button>
                    <button
                      className="px-3 py-1.5 rounded bg-red-500/80 hover:bg-red-500 text-slate-900 font-semibold disabled:opacity-50"
                      disabled={processing}
                      onClick={() => onReject?.(row)}
                      title="Reject"
                    >
                      Reject
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}

          {rows.length === 0 && (
            <tr>
              <td
                colSpan={showActions ? 9 : 8}
                className="px-4 py-6 text-center text-slate-400 text-sm"
              >
                No records.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DepositRequestsTable;
